<script setup lang="ts">
import { computed } from "vue";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VInlineButton from "@magnit/core/src/components/VInlineButton/VInlineButton.vue";
import MagnitIcon from "@magnit/icons/src/assets/icons/logo-magnit-16-colored-bg-invert.svg";
import CosmeticIcon from "@magnit/icons/src/assets/icons/logo-cosmetic-16-colored-bg-invert.svg";
import AptekaIcon from "@magnit/icons/src/assets/icons/logo-apteka-16-colored-bg-invert.svg";
import V1Icon from "@magnit/icons/src/assets/icons/logo-v1-36-color.svg";
import WatchesIcon from "@magnit/icons/src/assets/icons/watches-16.svg";
import SelectedIcon from "@magnit/icons/src/assets/icons/seen-24.svg";
import type { IShopSelectListItemProps } from "../types";
import { ShopSelectShopType, ShopSelectDisplayField } from "../enums";
import { getScheduleString } from "../utils";
import { SHOPS_NAMES_MAP } from "../constants";

const props = defineProps<IShopSelectListItemProps>();

const getIconComponentByType = (type: ShopSelectShopType) => {
  switch (type) {
    case ShopSelectShopType.AtHouse:
      return MagnitIcon;
    case ShopSelectShopType.Cosmetic:
      return CosmeticIcon;
    case ShopSelectShopType.Pharmacy:
      return AptekaIcon;
    case ShopSelectShopType.V1:
      return V1Icon;
    default:
      return MagnitIcon;
  }
};
const emit = defineEmits<{
  "click:store": [];
  "click:select": [];
}>();
const icon = computed(() => getIconComponentByType(props.type));
const schedule = computed(() =>
  getScheduleString({
    openingHours: props.openingHours,
    closingHours: props.closingHours,
  }),
);
const shopTypeTitle = computed(() => SHOPS_NAMES_MAP[props.type]);
const topLabel = computed(() => {
  const field = props.displayFields[0];
  return field === ShopSelectDisplayField.StoreType
    ? shopTypeTitle.value
    : props.address;
});
const bottomLabel = computed(() => {
  const field = props.displayFields[1];
  return field === ShopSelectDisplayField.Address
    ? props.address
    : schedule.value;
});
const displayWatchesIcon = computed(() => bottomLabel.value === schedule.value);

const handleItemClick = () => emit("click:store");
const handleSelectClick = () => emit("click:select");
</script>

<template>
  <div class="shop-select__list-item">
    <VText
      v-if="title"
      tag="p"
      font="title-small"
      class="shop-select__list-item--section-title"
      :title="title"
      >{{ title }}</VText
    >
    <section class="shop-select__list-item--inner" @click="handleItemClick">
      <div class="shop-select__list-item--icon">
        <component :is="icon" />
      </div>
      <div class="shop-select__list-item--info">
        <VText
          tag="p"
          font="body-large-accent"
          class="shop-select__list-item--top-label"
          :title="topLabel"
          >{{ topLabel }}</VText
        >
        <VText
          tag="p"
          font="body-small-regular-low"
          class="shop-select__list-item--bottom-label"
          :title="bottomLabel"
        >
          <WatchesIcon
            v-if="displayWatchesIcon"
            class="shop-select__list-item--bottom-label-icon"
          />
          {{ bottomLabel }}
        </VText>
      </div>
      <div class="shop-select__list-item--action">
        <div v-if="selected" class="shop-select__list-item--action-active">
          <SelectedIcon />
        </div>
        <VInlineButton
          v-else
          font="body-small-accent"
          class="shop-select__list-item--action-select"
          @click.stop="handleSelectClick"
        >
          <VText font="body-small-accent" color="accent">Выбрать</VText>
        </VInlineButton>
      </div>
    </section>
  </div>
</template>

<style lang="postcss" scoped>
.shop-select__list-item {
  padding-bottom: var(--pl-unit-x2);

  &--section-title {
    padding-top: var(--pl-unit-x4);
  }

  &--title-secondary {
    .shop-select__list-item--section-title {
      padding-top: var(--pl-unit-x2);
    }
  }

  &--inner {
    display: flex;
    cursor: pointer;
    padding: 2px 0 1px;
    border-bottom: 1px solid var(--pl-divider-primary);
  }

  &--last {
    padding-bottom: 0;

    .shop-select__list-item--inner {
      border-bottom: 0;
    }
  }

  &--icon {
    width: 16px;
    height: 16px;
    padding: var(--pl-unit-x2) 0;
    margin-right: var(--pl-unit-x4);

    > svg {
      width: 16px;
    }
  }

  &--info {
    display: flex;
    flex-direction: column;
    padding: var(--pl-unit-x2) 0;
    flex-grow: 1;
    max-width: calc(100% - 108px);

    @media (--pl-viewport-from-ml) {
      max-width: 217px;
    }
  }

  &--top-label,
  &--bottom-label {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &--bottom-label {
    margin: 0;
    margin-top: var(--pl-unit-x1);

    &-icon {
      position: relative;
      top: 2px;
    }
  }

  &--action {
    margin-left: var(--pl-unit-x3);
    width: 60px;

    &-select {
      padding: var(--pl-unit-x2) 0;
    }

    &-active {
      padding: var(--pl-unit-x2) 0;
      display: flex;
      justify-content: flex-end;
    }
  }
}
</style>
